import { Button, Field, Input } from "@norican/react";
import { useState } from "react";
import { Link } from "react-router";

import { HelpTerm } from "@/components/help-term";
import { Notice, Page } from "@/components/page";
import { PersonPicker } from "@/components/person-picker";
import { SeverityText } from "@/components/status";
import { control, subText, table, td, th } from "@/components/table";
import {
  type RiskSeverity,
  useCreateRiskFlag,
  useExpireRiskFlag,
  useMe,
  useRiskFlags,
} from "@/lib/api";
import { mayRaise, READ_ONLY_NOTE } from "@/lib/capability";
import { dateInputValue, dateInZone, daysRemaining, errorMessage } from "@/lib/format";
import { SEVERITY, SEVERITY_ORDER } from "@/lib/severity";

/**
 * Risk flags — HR-flagged cases, always time-boxed.
 *
 * Two rules are visible in the UI rather than hidden in the backend: the reason
 * is a closed list of codes (a free-text box here would collect speculation
 * about people), and every flag carries an end date, so there is no such thing
 * as a permanent list.
 *
 * Deliberately plain. This screen names people under suspicion; it is a record,
 * not a threat board — which is why severity is text at the weight of the rest
 * of the row and not a coloured pill.
 */

// Mirrors REASON_CODES in `backend/src/hrops/api/routes_flags.py`. Keep both in
// step: the backend rejects anything outside its list.
const REASON_CODES = [
  { value: "resignation_expected", label: "Resignation expected" },
  { value: "notice_period", label: "Notice period" },
  { value: "performance_process", label: "Performance process" },
  { value: "role_change", label: "Role change" },
  { value: "other", label: "Other" },
] as const;

const DEFAULT_WINDOW_DAYS = 30;

// Mirrors the CHECK constraint on risk_flag.note and the max_length on
// RiskFlagCreateRequest. Enforced in three places on purpose: the browser stops
// the typing, the API returns 422, the database refuses outright.
const NOTE_MAX = 500;

function reasonLabel(code: string): string {
  return REASON_CODES.find((reason) => reason.value === code)?.label ?? code;
}

export function FlagsPage() {
  const [includeExpired, setIncludeExpired] = useState(false);
  const { data, isLoading, error } = useRiskFlags(includeExpired);
  const expireFlag = useExpireRiskFlag();
  const canRaise = mayRaise(useMe().data);

  const flags = data?.items ?? [];

  return (
    <Page
      title="Risk flags"
      intro={
        <>
          A <HelpTerm id="risk-flag">flag</HelpTerm> records that HR is watching a situation for a
          fixed period, and every flag stops applying on its end date. Low and medium change nothing
          for the person. High restricts what they can do, and they will notice —{" "}
          <Link to="/help#help-severity" className="underline">
            what each severity does
          </Link>
          .
        </>
      }
      actions={
        <label className="text-fg-muted flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={(event) => setIncludeExpired(event.target.checked)}
            className="accent-fg"
          />
          Show ended and retired flags
        </label>
      }
    >
      {canRaise ? (
        <CreateFlagForm />
      ) : (
        <p className={`${subText} border-border mt-6 border-t pt-4`}>{READ_ONLY_NOTE}</p>
      )}

      <section className="mt-8">
        <h2 className="text-fg text-sm font-semibold">
          {includeExpired ? "All flags" : "Active flags"}
          <span className="text-fg-muted ml-2 font-normal">{flags.length}</span>
        </h2>

        {error ? (
          <Notice tone="danger">{errorMessage(error, "Could not load the flags.")}</Notice>
        ) : isLoading ? (
          <Notice>Loading the flags…</Notice>
        ) : flags.length === 0 ? (
          <Notice>{includeExpired ? "No flags have been raised." : "No active flags."}</Notice>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className={table}>
              <thead>
                <tr>
                  <th scope="col" className={th}>
                    Person
                  </th>
                  <th scope="col" className={th}>
                    Reason
                  </th>
                  <th scope="col" className={th}>
                    Severity
                  </th>
                  <th scope="col" className={th}>
                    Window
                  </th>
                  <th scope="col" className={th}>
                    Remaining
                  </th>
                  <th scope="col" className={th}>
                    Raised by
                  </th>
                  <th scope="col" className={th}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {flags.map((flag) => {
                  const remaining = daysRemaining(flag.ends_at);
                  const ended = Boolean(flag.expired_at) || remaining <= 0;
                  return (
                    <tr key={flag.id}>
                      <td className={`${td} text-fg`}>{flag.subject_upn}</td>
                      <td className={`${td} text-fg`}>
                        {reasonLabel(flag.reason_code)}
                        {flag.note ? (
                          <span className="text-fg-muted mt-0.5 block max-w-[28rem] text-xs whitespace-normal">
                            {flag.note}
                          </span>
                        ) : null}
                      </td>
                      <td className={`${td} text-fg`}>
                        <SeverityText severity={flag.severity} />
                        <div className={subText}>{SEVERITY[flag.severity].summary}</div>
                      </td>
                      <td className={`${td} text-fg whitespace-nowrap`}>
                        {dateInZone(flag.starts_at)} to {dateInZone(flag.ends_at)}
                      </td>
                      <td className={`${td} whitespace-nowrap`}>
                        {flag.expired_at ? (
                          <span className={subText}>retired {dateInZone(flag.expired_at)}</span>
                        ) : remaining <= 0 ? (
                          <span className={subText}>ended</span>
                        ) : (
                          <span className="text-fg tabular-nums">
                            {remaining} {remaining === 1 ? "day" : "days"}
                          </span>
                        )}
                      </td>
                      <td className={`${td} text-fg`}>{flag.requester_upn}</td>
                      <td className={`${td} text-right`}>
                        {ended || !canRaise ? null : (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={expireFlag.isPending}
                            onClick={() => expireFlag.mutate({ flagId: flag.id })}
                          >
                            Retire now
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {expireFlag.isError ? (
          <Notice tone="danger">
            {errorMessage(expireFlag.error, "The flag could not be retired.")}
          </Notice>
        ) : null}
      </section>
    </Page>
  );
}

function CreateFlagForm() {
  const createFlag = useCreateRiskFlag();
  const [subjectUpn, setSubjectUpn] = useState("");
  const [reasonCode, setReasonCode] = useState<string>(REASON_CODES[0].value);
  const [severity, setSeverity] = useState<RiskSeverity>("medium");
  const [endsOn, setEndsOn] = useState(() => dateInputValue(DEFAULT_WINDOW_DAYS));
  const [note, setNote] = useState("");

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!subjectUpn.trim() || !endsOn) {
      return;
    }
    createFlag.mutate(
      {
        subject_upn: subjectUpn.trim(),
        reason_code: reasonCode,
        severity,
        // End of the chosen day, in the browser's zone.
        ends_at: new Date(`${endsOn}T23:59:59`).toISOString(),
        // Empty stays empty rather than becoming "", so the record does not
        // claim somebody wrote a comment and left it blank.
        note: note.trim() || undefined,
      },
      {
        onSuccess: () => {
          setSubjectUpn("");
          setReasonCode(REASON_CODES[0].value);
          setSeverity("medium");
          setEndsOn(dateInputValue(DEFAULT_WINDOW_DAYS));
          setNote("");
        },
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="border-border mt-6 flex flex-wrap items-end gap-4 border-t pt-4"
    >
      {/* The same picker the offboarding form uses. A flag names a person, and
          naming the wrong one puts a colleague under suspicion — or, at high
          severity, restricts them. */}
      <PersonPicker
        label="Person"
        required
        size="sm"
        className="min-w-[18rem] flex-1"
        placeholder="Surname, or firstname.lastname@noricangroup.com"
        value={subjectUpn}
        onChange={setSubjectUpn}
      />

      <label className="text-fg-muted flex flex-col gap-1 text-xs">
        Reason
        <select
          value={reasonCode}
          onChange={(event) => setReasonCode(event.target.value)}
          className={control}
        >
          {REASON_CODES.map((reason) => (
            <option key={reason.value} value={reason.value}>
              {reason.label}
            </option>
          ))}
        </select>
      </label>

      <label className="text-fg-muted flex flex-col gap-1 text-xs">
        Severity
        <select
          value={severity}
          onChange={(event) => setSeverity(event.target.value as RiskSeverity)}
          className={control}
        >
          {SEVERITY_ORDER.map((value) => (
            <option key={value} value={value}>
              {SEVERITY[value].label} — {SEVERITY[value].summary}
            </option>
          ))}
        </select>
      </label>

      <Field
        label="Ends on"
        required
        hint={`Every flag expires. Default ${DEFAULT_WINDOW_DAYS} days.`}
        className="min-w-[12rem]"
      >
        <Input
          size="sm"
          type="date"
          required
          min={dateInputValue(1)}
          value={endsOn}
          onChange={(event) => setEndsOn(event.target.value)}
        />
      </Field>

      {/* The consequence of the choice, stated where the choice is made. A
          severity that is only a word cannot be chosen correctly. */}
      <div className="w-full">
        <p className={subText}>
          <span className="text-fg">{SEVERITY[severity].label}:</span>{" "}
          {SEVERITY[severity].effects.join(" ")}
        </p>
        {SEVERITY[severity].caution ? (
          <p className="text-warning-fg border-warning-border mt-2 border-l-2 pl-2 text-xs">
            {SEVERITY[severity].caution}
          </p>
        ) : null}
      </div>

      {/* Full width so it wraps onto its own row: a comment is the one field
          here that needs room, and squeezing it beside the selects would invite
          one-word entries that carry no context. */}
      <Field
        label="Comment"
        hint={`Optional. Context a reason code cannot carry. ${NOTE_MAX} characters, expires with the flag.`}
        className="w-full"
      >
        <textarea
          rows={2}
          maxLength={NOTE_MAX}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="e.g. Leaving 31 Oct, notice served. Asked for an export of the customer list on 12 Sep."
          className="border-border bg-bg text-fg placeholder:text-fg-muted/60 w-full rounded border px-2 py-1.5 text-sm"
        />
      </Field>

      <Button type="submit" disabled={createFlag.isPending} loading={createFlag.isPending}>
        Raise flag
      </Button>

      {createFlag.isError ? (
        <p className="text-danger-fg basis-full text-sm">
          {errorMessage(createFlag.error, "The flag could not be saved.")}
        </p>
      ) : null}
    </form>
  );
}
